import { Box, Stack, Tooltip, Typography } from '@mui/material';
import {
  type RestoreAccount,
  type SourceAccount,
  AccountState,
  type Account,
  Provider,
  CloudAccountType,
} from '@repo/api-gw-sdk';
import semver from 'semver';

import { getDocsUrl } from '@/docs/docsUrls';
import {
  getAccountProvider,
  getAccountState,
  getAccountType,
} from '@/utils/accounts';
import {
  getAccountInstalledVersion,
  getAccountLatestVersion,
  isAccountRoleMajorMinorVersionOld,
} from '@/utils/roles';

import { Icon } from '../shared/icon';
import { ReadMoreLink } from '../shared/readMoreLink';

interface ChangelogKeys {
  source: Parameters<typeof getDocsUrl>[0];
  restore: Parameters<typeof getDocsUrl>[0];
}

const changelogKeyMap: Partial<Record<Provider, ChangelogKeys>> = {
  [Provider.Aws]: {
    source: 'awsSourcePermissionsChangelog',
    restore: 'awsRestorePermissionsChangelog',
  },
  [Provider.Gcp]: {
    source: 'gcpSourcePermissionsChangelog',
    restore: 'gcpRestorePermissionsChangelog',
  },
  [Provider.Azure]: {
    source: 'azureSourcePermissionsChangelog',
    restore: 'azureRestorePermissionsChangelog',
  },
};

export const EonRoleVersion = ({
  account,
}: {
  account: Account | SourceAccount | RestoreAccount;
}) => {
  const accountState = getAccountState(account);
  if (accountState === AccountState.ACCOUNT_STATE_INSUFFICIENT_PERMISSIONS) {
    return <Typography>--</Typography>;
  }

  const showWarning = isAccountRoleMajorMinorVersionOld(account);
  const installedVersion = getAccountInstalledVersion(account);
  const latestVersion = getAccountLatestVersion(account);

  const provider = getAccountProvider(account);
  const accountTypeKey =
    getAccountType(account) === CloudAccountType.Source ? 'source' : 'restore';
  const docsKey = changelogKeyMap[provider]?.[accountTypeKey];
  const changelogUrl = docsKey ? getDocsUrl(docsKey) : undefined;

  return (
    <Stack
      direction='row'
      sx={{
        alignItems: 'center',
        gap: '12px',
      }}
    >
      <Typography className={'inline-block'}>
        {installedVersion && semver.valid(installedVersion)
          ? installedVersion
          : '1.0.0'}
      </Typography>
      {showWarning && (
        <Tooltip
          disableInteractive={false}
          title={
            <Stack
              sx={{
                gap: '4px',
              }}
            >
              <Box component='span'>
                v{latestVersion} is available. Upgrade permissions to enable
                Eon&apos;s latest features.
              </Box>
              {changelogUrl && (
                <ReadMoreLink
                  text='Go to the changelog'
                  href={changelogUrl}
                  className='text-[inherit]!'
                />
              )}
            </Stack>
          }
        >
          <Icon
            iconClass={
              'material-symbols-error-outline-rounded h-[20px] w-[20px]'
            }
          />
        </Tooltip>
      )}
    </Stack>
  );
};
